import React from "react";
import PropTypes from "prop-types";

import { useSync } from "data/hub/sync";

import { useBooleanState } from "hooks";

import { useUserMenuManager } from "../../hooks";
import {
  Button,
  FeatureFlag,
  SendInvitationButton,
  UserInfoActionMenu,
} from "..";

export function UserMenu({
  user,
  onEditEmail,
  onEditUser,
  onEditPassword,
  onInviteSent,
  onReplaceUser,
  onReassignTask,
  onUpdateInsightsLicense,
  onDeactivateUser,
  onReactivateUser,
}) {
  const [isOpen, open, close] = useBooleanState();
  const { showButton } = useUserMenuManager(user);
  const { syncInProgress } = useSync();

  return (
    <UserInfoActionMenu
      userId={user.id}
      isOpen={isOpen}
      onMenuOpen={open}
      onMenuClose={close}
    >
      <React.Fragment>
        {showButton.editEmail && (
          <Button
            key="Edit Email Address"
            icon="envelope"
            content="Edit Email Address"
            onClick={() => {
              close();
              onEditEmail(user);
            }}
          />
        )}
        <Button
          key="Edit User Information"
          icon="edit"
          content="Edit User Information"
          onClick={() => {
            close();
            onEditUser(user);
          }}
        />
        {showButton.editPassword && (
          <Button
            key="Change Password"
            icon="key"
            content="Change Password"
            onClick={() => {
              close();
              onEditPassword(user);
            }}
          />
        )}
        <FeatureFlag id="add-users">
          {showButton.sendInvite && (
            <SendInvitationButton
              user={user}
              onInvitationSent={() => {
                close();
                onInviteSent(user);
              }}
            />
          )}
        </FeatureFlag>
        {showButton.enableInsights && (
          <Button
            key="Insights License"
            icon="chart pie"
            content={`${user.insightsEnabled ? "Disable" : "Enable"} Insights`}
            onClick={() => {
              close();
              onUpdateInsightsLicense(user);
            }}
          />
        )}
        {showButton.reassignTasks && (
          <Button
            key="Reassign Tasks"
            disabled={syncInProgress}
            icon="tasks"
            content="Reassign Tasks"
            onClick={() => {
              close();
              onReassignTask(user);
            }}
          />
        )}
        {showButton.replaceUser && (
          <Button
            key="Replace User"
            icon="user"
            disabled={syncInProgress}
            content="Replace User"
            onClick={() => {
              close();
              onReplaceUser(user);
            }}
          />
        )}

        {showButton.deactivateUser && (
          <Button
            key="Deactivate User"
            icon="remove"
            disabled={syncInProgress}
            content="Deactivate User"
            onClick={() => {
              close();
              onDeactivateUser(user);
            }}
          />
        )}

        {showButton.reactivateUser && (
          <Button
            key="Reactivate User"
            icon="repeat"
            disabled={syncInProgress}
            content="Reactivate User"
            onClick={() => {
              close();
              onReactivateUser(user);
            }}
          />
        )}
      </React.Fragment>
    </UserInfoActionMenu>
  );
}

UserMenu.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    insightsEnabled: PropTypes.bool.isRequired,
  }).isRequired,
  onEditEmail: PropTypes.func.isRequired,
  onEditUser: PropTypes.func.isRequired,
  onEditPassword: PropTypes.func.isRequired,
  onInviteSent: PropTypes.func.isRequired,
  onReplaceUser: PropTypes.func.isRequired,
  onReassignTask: PropTypes.func.isRequired,
  onUpdateInsightsLicense: PropTypes.func.isRequired,
  onDeactivateUser: PropTypes.func.isRequired,
  onReactivateUser: PropTypes.func.isRequired,
};

export default UserMenu;
